import URI from 'jsuri';

import API from 'js/lib/api';

import type { ProductType } from 'bundles/payments/common/ProductType';
import ProductTypeObject from 'bundles/payments/common/ProductType';
import type { NonTerminalSurveyData, SurveyData } from 'bundles/refund/common/types';
import CourseraPlusUpgradeV2 from 'bundles/subscriptions/api/courseraPlusUpgradeV2';
import { QuestionTypes, ResponseTypes } from 'bundles/survey-form/constants/SurveyFormConstants';
import type { QuestionType } from 'bundles/survey-form/constants/SurveyFormConstants';
import type SurveyResponseActionType from 'bundles/survey-form/constants/SurveyResponseActionType';

const {
  SPECIALIZATION,
  SPECIALIZATION_SUBSCRIPTION,
  COURSERA_PLUS_SUBSCRIPTION,
  COURSERA_TIER_LITE,
  VERIFIED_CERTIFICATE,
} = ProductTypeObject;

const learnerExitSurveyResponsesAPI = API('/api/learnerExitSurveyResponses.v1', { type: 'rest' });

const questionResponseTypeMap = {
  [QuestionTypes.MULTIPLE_CHOICE]: ResponseTypes.MULTIPLE_CHOICE_RESPONSE,
  [QuestionTypes.CHECKBOX]: ResponseTypes.CHECKBOX_RESPONSE,
  [QuestionTypes.SHORT_ANSWER]: ResponseTypes.SHORT_ANSWER_RESPONSE,
  [QuestionTypes.NUMERIC]: ResponseTypes.NUMERIC_RESPONSE,
  [QuestionTypes.PARAGRAPH_ANSWER]: ResponseTypes.PARAGRAPH_ANSWER_RESPONSE,
  [QuestionTypes.SINGLE_CHECKBOX]: ResponseTypes.SINGLE_CHECKBOX_RESPONSE,
  [QuestionTypes.TYPEAHEAD]: ResponseTypes.TYPEAHEAD_RESPONSE,
  [QuestionTypes.DROPDOWN]: ResponseTypes.DROPDOWN_RESPONSE,
};

const COURSERA_PLUS_METADATA_TYPENAME = 'courseraPlusLearnerExitSurveyResponseMetadata';

type SurveyResponse = {
  elements: Array<{
    responseId: string;
    sectionId: string;
    section: {
      isTerminal: boolean;
      title: string;
      questions: Array<{
        id: string;
        content: {
          [key: string]: {
            prompt: string;
            options: Array<{
              id: string;
              text: string;
            }>;
          };
        };
      }>;
    };
  }>;
};

type SurveyCreateRequest = {
  productType: string;
  subscriptionId?: string;
  underlyingProductItemId?: string;
  productMetadata?: {
    typeName: string;
    definition: {
      [key: string]: string;
    };
  };
};

type SurveyUpdateRequest = {
  responses: {
    [key: string]: {
      [key: string]: {
        [key: string]: string | Array<string>;
      };
    };
  };
  sectionId: string;
};

export const formatSurveyResponse = (response?: SurveyResponse): SurveyData => {
  if (!response || response.elements.length === 0) {
    throw new Error('No survey response available');
  }

  const data = response.elements[0];
  const { responseId, sectionId, section } = data;
  const { isTerminal, title } = section;

  if (isTerminal) {
    return {
      responseId,
      sectionId,
      isTerminal,
    };
  }

  const question = section.questions[0];
  const questionType = Object.keys(question.content)[0] as QuestionType;
  const content = question.content[questionType];
  // short answer is freeform - disregard the BE response for the options in this case
  const options = questionType === QuestionTypes.SHORT_ANSWER ? [] : content.options;

  return {
    responseId,
    sectionId,
    isTerminal,
    title,
    prompt: content.prompt,
    questionType,
    options,
    questionId: question.id,
  };
};

const formatQuestionResponse = (
  responseType: (typeof ResponseTypes)[keyof typeof ResponseTypes],
  answer?: string | Array<string>
): Record<string, string | Array<string>> => {
  switch (responseType) {
    case ResponseTypes.MULTIPLE_CHOICE_RESPONSE:
      return { id: answer || '' };
    case ResponseTypes.CHECKBOX_RESPONSE:
      return { ids: answer || [] };
    case ResponseTypes.SHORT_ANSWER_RESPONSE:
      return { text: answer || '' };
    default:
      throw new Error(`Cannot format unsupported question response type: ${responseType}`);
  }
};

export const createSurvey = ({
  productType,
  productItemId,
  subscriptionId,
  surveyResponseActionType,
}: {
  productType: ProductType;
  productItemId?: string;
  subscriptionId?: string;
  surveyResponseActionType: keyof typeof SurveyResponseActionType;
}): Promise<SurveyData> => {
  const data: SurveyCreateRequest = { productType };

  if (productType === SPECIALIZATION || productType === VERIFIED_CERTIFICATE) {
    data.underlyingProductItemId = productItemId;
  } else if (productType === SPECIALIZATION_SUBSCRIPTION) {
    data.underlyingProductItemId = productItemId;
    data.subscriptionId = subscriptionId;
  } else if (productType === COURSERA_PLUS_SUBSCRIPTION) {
    data.subscriptionId = subscriptionId;
    data.productMetadata = {
      typeName: COURSERA_PLUS_METADATA_TYPENAME,
      definition: {
        courseraPlusActionType: surveyResponseActionType,
      },
    };
  } else if (productType === COURSERA_TIER_LITE) {
    data.subscriptionId = subscriptionId;
  }

  return new Promise((resolve, reject) => {
    const uri = new URI().addQueryParam('fields', 'section,sectionId');
    learnerExitSurveyResponsesAPI.post(uri.toString(), { data }).then(formatSurveyResponse).then(resolve).catch(reject);
  });
};

export const updateSurvey = ({
  responseId,
  sectionId,
  questionId,
  questionType,
  answer,
}: Pick<NonTerminalSurveyData, 'responseId' | 'sectionId' | 'questionId' | 'questionType'> & {
  answer?: string | Array<string>;
}): Promise<SurveyData> => {
  const responseType = questionResponseTypeMap[questionType];

  const data: SurveyUpdateRequest = {
    responses: {
      [questionId]: {
        [responseType]: formatQuestionResponse(responseType, answer),
      },
    },
    sectionId,
  };
  const uri = new URI(responseId).addQueryParam('fields', 'section,sectionId');
  return Promise.resolve(learnerExitSurveyResponsesAPI.put(uri.toString(), { data }).then(formatSurveyResponse));
};

type DiscountPromoCode = {
  code: string;
  promotionId: number;
};

export type CourseraPlusUpgradeAction = {
  productId: string;
  countryIsoCode: string;
  upgradeOfferType: string;
  userId: number;
  currencyCode: string;
};

const offerTypes = { discount: 'DISCOUNT', courseraPlus: 'COURSERAPLUS' };

type DeflectionOffer = {
  id: string;
  offerType: string;
  discountPromoCode?: DiscountPromoCode;
  courseraPlusUpgradeAction?: CourseraPlusUpgradeAction;
};

export interface DeflectionResult {
  elements: DeflectionOffer[];
}

export type DeflectionProps = {
  deflectionPromoCode?: string;
  deflectionUpsell?: CourseraPlusUpgradeAction;
};

export async function getDeflectionOffers(subscriptionId: string) {
  const urlString = new URI('/api/cancellationDeflection.v2')
    .addQueryParam('q', 'findBySubscriptionId')
    .addQueryParam('id', subscriptionId)
    .addQueryParam('fields', 'courseraPlusUpgradeAction,discountPromoCode,id,offerType')
    .toString();
  const api = API('', { type: 'rest' });
  const result = await api.get(urlString);
  return result as DeflectionResult;
}

export const getPromoCodeFromDeflection = (deflection: DeflectionResult | undefined): string | undefined => {
  if (!deflection || !Array.isArray(deflection.elements)) {
    return undefined;
  }
  const discountOffer = deflection.elements.find((e) => e.offerType === offerTypes.discount);
  return discountOffer?.discountPromoCode?.code;
};

export const getUpgradeActionFromDeflection = (
  deflection: DeflectionResult | undefined
): CourseraPlusUpgradeAction | undefined => {
  if (!deflection || !Array.isArray(deflection.elements)) {
    return undefined;
  }
  const upgradeActionElement = deflection.elements.find((e) => e.offerType === offerTypes.courseraPlus);
  return upgradeActionElement?.courseraPlusUpgradeAction;
};

export const getPropertiesForDeflection = (deflection: DeflectionResult): DeflectionProps => {
  const deflectionPromoCode = getPromoCodeFromDeflection(deflection);
  const deflectionUpsell = getUpgradeActionFromDeflection(deflection);
  if (!deflectionPromoCode && !deflectionUpsell) {
    return {};
  }
  if (deflectionPromoCode && !deflectionUpsell) {
    return { deflectionPromoCode };
  }
  if (!deflectionPromoCode && deflectionUpsell) {
    return { deflectionUpsell };
  }

  return { deflectionPromoCode };
};

export const s12nUpgrade = (upgrade: CourseraPlusUpgradeAction) => {
  const uri = new URI().addQueryParam('action', 'midCycleUpgrade');
  return CourseraPlusUpgradeV2.post(uri.toString(), { data: upgrade });
};
